import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertTriangle, Settings, Download, Loader2, Search,
  Euro, MousePointerClick, Target, TrendingUp, Trophy, AlertOctagon,
  Wallet, Sparkles,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, BarChart, Bar,
} from 'recharts';
import TopCreativesTable from '../../components/sea/TopCreativesTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: 'LAST_7_DAYS', label: '7 derniers jours' },
  { value: 'LAST_14_DAYS', label: '14 derniers jours' },
  { value: 'LAST_30_DAYS', label: '30 derniers jours' },
  { value: 'THIS_MONTH', label: 'Ce mois' },
  { value: 'LAST_MONTH', label: 'Mois dernier' },
  { value: 'LAST_90_DAYS', label: '90 derniers jours' },
];

function periodToDates(period) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const sub = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (period) {
    case 'LAST_7_DAYS': return [fmt(sub(7)), fmt(sub(1))];
    case 'LAST_14_DAYS': return [fmt(sub(14)), fmt(sub(1))];
    case 'LAST_30_DAYS': return [fmt(sub(30)), fmt(sub(1))];
    case 'LAST_90_DAYS': return [fmt(sub(90)), fmt(sub(1))];
    case 'THIS_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return [fmt(start), fmt(today)];
    }
    case 'LAST_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return [fmt(start), fmt(end)];
    }
    default: return [fmt(sub(30)), fmt(sub(1))];
  }
}

const formatEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const formatEURShort = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const formatNum = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const formatPct = (n) => `${((n || 0) * 100).toFixed(2)}%`;
const formatRoas = (n) => {
  if (n == null) return '—';
  if (n === 0) return '0.00×';
  if (n > 0 && n < 0.01) return '<0.01×';
  return `${n.toFixed(2)}×`;
};

// ─────────────────────────────────────────────────────────────
// localStorage cache for SEA lead analytics
// Stale-while-revalidate: render cached payload immediately, refetch
// in background, replace once fresh data lands. TTL 10 min, capped
// at 8 most-recent (client+period) combos.
// ─────────────────────────────────────────────────────────────
const SEA_CACHE_KEY = 'aegis:sea-lead-analytics:cache:v1';
const SEA_CACHE_TTL_MS = 10 * 60 * 1000;
const seaCacheKey = (client, period) => `${client}|${period}`;

const loadSeaCache = (key) => {
  try {
    const raw = localStorage.getItem(SEA_CACHE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    const entry = all[key];
    if (!entry) return null;
    if (Date.now() - entry.t > SEA_CACHE_TTL_MS) return null;
    return entry.data;
  } catch {
    return null;
  }
};

const saveSeaCache = (key, data) => {
  try {
    const raw = localStorage.getItem(SEA_CACHE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[key] = { t: Date.now(), data };
    const keys = Object.keys(all).sort((a, b) => all[b].t - all[a].t).slice(0, 8);
    const trimmed = {};
    keys.forEach(k => { trimmed[k] = all[k]; });
    localStorage.setItem(SEA_CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    // quota exceeded — silent
  }
};

export default function SEALeadAnalytics() {
  const [period, setPeriod] = useState('LAST_30_DAYS');
  const [config, setConfig] = useState(null);
  const [seaClients, setSeaClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('top'); // 'top' | 'worst' | 'all'
  const [search, setSearch] = useState('');

  const [start, end] = useMemo(() => periodToDates(period), [period]);

  // Initial load: config + clients
  useEffect(() => {
    (async () => {
      try {
        const [cfg, cls] = await Promise.all([
          axios.get(`${API}/google-ads/config`, { withCredentials: true }),
          axios.get(`${API}/clients`, { withCredentials: true }),
        ]);
        setConfig(cfg.data);
        const linked = (cls.data || []).filter(c => c.google_ads_customer_id);
        setSeaClients(linked);
      } catch (e) {
        setConfig({ is_connected: false });
      }
    })();
  }, []);

  // Load analytics — stale-while-revalidate via localStorage
  useEffect(() => {
    if (!config?.is_connected || seaClients.length === 0) return;
    let cancel = false;

    const ck = seaCacheKey(selectedClient, period);
    const cached = loadSeaCache(ck);
    if (cached) {
      setData(cached);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
      setRefreshing(false);
    }

    (async () => {
      try {
        const params = new URLSearchParams({ start, end, keyword_limit: '200' });
        if (selectedClient !== 'all') params.set('client_id', selectedClient);
        const { data: fresh } = await axios.get(`${API}/google-ads/lead-analytics?${params}`, { withCredentials: true });
        if (cancel) return;
        setData(fresh);
        saveSeaCache(ck, fresh);
      } catch (e) {
        if (!cancel && !cached) toast.error(e.response?.data?.detail || 'Erreur lors du chargement de l\'analyse');
      } finally {
        if (!cancel) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => { cancel = true; };
  }, [selectedClient, period, start, end, config?.is_connected, seaClients.length]);

  const exportCsv = (rows, filename) => {
    if (!rows || !rows.length) return;
    const cols = [
      { label: 'Mot-clé', get: r => r.keyword },
      { label: 'Match', get: r => r.match_type },
      { label: 'QS', get: r => r.quality_score ?? '' },
      { label: 'Client', get: r => r.client_name || '' },
      { label: 'Campagne', get: r => r.campaign_name },
      { label: 'AdGroup', get: r => r.ad_group_name },
      { label: 'Impressions', get: r => r.impressions },
      { label: 'Clics', get: r => r.clicks },
      { label: 'CTR', get: r => formatPct(r.ctr) },
      { label: 'CPC moy.', get: r => (r.average_cpc || 0).toFixed(2) },
      { label: 'Coût (€)', get: r => (r.cost || 0).toFixed(2) },
      { label: 'Conversions', get: r => (r.conversions || 0).toFixed(1) },
      { label: 'Valeur conv. (€)', get: r => (r.conversions_value || 0).toFixed(2) },
      { label: 'Taux conv.', get: r => formatPct(r.conversion_rate) },
      { label: 'CPA (€)', get: r => (r.cost_per_conversion || 0).toFixed(2) },
      { label: 'ROAS', get: r => (r.roas || 0).toFixed(2) },
    ];
    const header = cols.map(c => c.label).join(';');
    const lines = rows.map(r => cols.map(c => {
      const val = c.get(r);
      const str = String(val ?? '');
      return /[;\n"]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(';'));
    const csv = '\uFEFF' + [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Loading state
  if (!config) {
    return <Layout><div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div></Layout>;
  }

  // Not connected
  if (!config.is_connected) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="sea-lead-analytics-disconnected">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Analyse leads SEA</h1>
            <p className="text-neutral-500 mt-1">CPA, ROAS et taux de conversion par mot-clé</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="font-heading font-semibold text-lg text-neutral-900">Google Ads non connecté</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Connectez votre compte Google Ads pour activer l'analyse des leads et des performances par mot-clé.
                </p>
                <Link to="/settings"><Button className="bg-orange-600 hover:bg-orange-700 mt-3"><Settings className="w-4 h-4 mr-1" /> Configurer Google Ads</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // No SEA clients linked
  if (seaClients.length === 0) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="sea-lead-analytics-empty">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Analyse leads SEA</h1>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-5 text-sm text-blue-900">
            Aucun client n'est lié à un compte Google Ads. Allez dans le <Link to="/sea/dashboard" className="underline font-medium">Tableau de bord SEA</Link> et cliquez sur <em>Lier comptes</em>.
          </div>
        </div>
      </Layout>
    );
  }

  const summary = data?.summary;
  const tabRows = !data ? [] : (
    tab === 'top' ? data.top_keywords :
    tab === 'worst' ? data.worst_keywords :
    data.all_keywords
  );
  const filteredRows = (tabRows || []).filter(k =>
    !search || k.keyword.toLowerCase().includes(search.toLowerCase()) ||
    (k.campaign_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (k.client_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-5" data-testid="sea-lead-analytics">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Analyse leads SEA</h1>
            <p className="text-neutral-500 mt-1">CPA, ROAS et taux de conversion par mot-clé · attribution closed-loop CRM</p>
          </div>
          <div className="flex items-center gap-2">
            {refreshing && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-500 mr-1" data-testid="sea-la-refreshing" title="Mise à jour en arrière-plan">
                <Loader2 className="w-3 h-3 animate-spin" />
                Actualisation…
              </span>
            )}
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-56" data-testid="sea-la-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients SEA</SelectItem>
                {seaClients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-44" data-testid="sea-la-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
        ) : !summary ? (
          <div className="bg-white border border-neutral-200 rounded p-8 text-sm text-neutral-500 text-center">
            Aucune donnée pour cette période.
          </div>
        ) : (
          <>
            {/* Ads-side KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sea-la-ads-kpis">
              <KpiCard
                icon={<Euro className="w-4 h-4" />}
                label="Coût total Ads"
                value={formatEURShort(summary.cost)}
                hint={`${formatNum(summary.clicks)} clics · ${formatNum(summary.impressions)} impr.`}
                testId="kpi-cost"
              />
              <KpiCard
                icon={<Target className="w-4 h-4" />}
                label="CPA (Ads)"
                value={summary.conversions ? formatEUR(summary.cost_per_conversion) : '—'}
                hint={`${summary.conversions.toFixed(1)} conversions`}
                color="orange"
                testId="kpi-cpa"
              />
              <KpiCard
                icon={<TrendingUp className="w-4 h-4" />}
                label="ROAS (Ads)"
                value={formatRoas(summary.roas)}
                hint={`${formatEURShort(summary.conversions_value)} valeur conv.`}
                color={summary.roas >= 1 ? 'emerald' : 'red'}
                testId="kpi-roas"
              />
              <KpiCard
                icon={<MousePointerClick className="w-4 h-4" />}
                label="Taux de conversion"
                value={formatPct(summary.conversion_rate)}
                hint={`${formatPct(summary.ctr)} CTR · ${formatEUR(summary.average_cpc)} CPC`}
                testId="kpi-conv-rate"
              />
            </div>

            {/* CRM closed-loop KPIs */}
            <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 rounded-lg p-4" data-testid="sea-la-crm-kpis">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-4 h-4 text-emerald-700" />
                <h3 className="font-heading font-semibold text-sm text-neutral-900">Attribution réelle CRM (closed-loop)</h3>
                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium uppercase flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" /> Sellsy + Webhooks
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat label="Leads CRM" value={formatNum(summary.real_leads)} hint={summary.clicks ? `${formatPct(summary.real_lead_rate)} des clics` : ''} />
                <MiniStat label="Ventes (won)" value={formatNum(summary.real_wins)} />
                <MiniStat label="Revenu attribué" value={formatEURShort(summary.real_revenue)} color="emerald" bold />
                <MiniStat
                  label="ROAS réel"
                  value={summary.real_roas == null ? '—' : formatRoas(summary.real_roas)}
                  hint={summary.real_cpa ? `CPA réel : ${formatEUR(summary.real_cpa)}` : ''}
                  color={summary.real_roas != null && summary.real_roas >= 1 ? 'emerald' : 'neutral'}
                  bold
                />
              </div>
              {summary.real_leads === 0 && (
                <p className="text-xs text-neutral-500 mt-3">
                  Aucun lead CRM matché sur la période. Pour activer l'attribution réelle :
                  configurez <Link to="/integrations" className="underline">Sellsy ou un webhook CRM</Link>,
                  puis synchronisez les leads dans <Link to="/lead-analytics" className="underline">Lead Analytics</Link>.
                </p>
              )}
            </div>

            {/* Time series */}
            {data.daily_timeseries && data.daily_timeseries.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="sea-la-chart">
                <h3 className="font-heading font-semibold text-sm text-neutral-900 mb-3">Évolution quotidienne · Coût vs Revenu</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.daily_timeseries} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `${Math.round(v)}€`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0A0A0A', border: 'none', borderRadius: 6, color: '#fff', fontSize: 11 }}
                      formatter={(value, name) => {
                        if (name === 'Coût Ads' || name === 'Revenu CRM') return [formatEUR(value), name];
                        return [formatNum(value), name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#f97316" strokeWidth={2} name="Coût Ads" dot={false} />
                    <Line yAxisId="left" type="monotone" dataKey="real_revenue" stroke="#10b981" strokeWidth={2} name="Revenu CRM" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="conversions" stroke="#6366f1" strokeWidth={1.5} name="Conv. Ads" dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Top campaigns */}
            {data.top_campaigns && data.top_campaigns.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="sea-la-campaigns">
                <h3 className="font-heading font-semibold text-sm text-neutral-900 mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-600" /> Top campagnes par ROAS
                </h3>
                <ResponsiveContainer width="100%" height={Math.min(400, 60 + data.top_campaigns.slice(0, 8).length * 36)}>
                  <BarChart data={data.top_campaigns.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis dataKey="campaign_name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={130} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0A0A0A', border: 'none', borderRadius: 6, color: '#fff', fontSize: 11 }}
                      formatter={(v, name) => [name === 'ROAS' ? `${v.toFixed(2)}×` : formatEUR(v), name]}
                    />
                    <Bar dataKey="roas" fill="#10b981" name="ROAS" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Keyword tables */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200">
                <div className="flex">
                  <TabButton active={tab === 'top'} onClick={() => setTab('top')} testId="sea-la-tab-top">
                    <Trophy className="w-3.5 h-3.5 mr-1" /> Top mots-clés ({data.top_keywords?.length || 0})
                  </TabButton>
                  <TabButton active={tab === 'worst'} onClick={() => setTab('worst')} testId="sea-la-tab-worst">
                    <AlertOctagon className="w-3.5 h-3.5 mr-1" /> Dépense gaspillée ({data.worst_keywords?.length || 0})
                  </TabButton>
                  <TabButton active={tab === 'all'} onClick={() => setTab('all')} testId="sea-la-tab-all">
                    Tous les mots-clés ({data.all_keywords?.length || 0})
                  </TabButton>
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher mot-clé / campagne…"
                      className="pl-7 w-72 h-8 text-sm"
                      data-testid="sea-la-search"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportCsv(filteredRows, `sea_lead_analytics_${tab}_${start}_${end}.csv`)}
                    data-testid="sea-la-export-csv"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> CSV
                  </Button>
                </div>
              </div>

              {tab === 'top' && data.top_keywords?.length === 0 ? (
                <div className="text-sm text-neutral-500 py-8 text-center bg-white border border-neutral-200 rounded">
                  Aucun mot-clé n'a généré de conversion sur cette période.
                </div>
              ) : tab === 'worst' && data.worst_keywords?.length === 0 ? (
                <div className="text-sm text-emerald-700 py-8 text-center bg-emerald-50 border border-emerald-200 rounded">
                  ✓ Aucun mot-clé sans conversion détecté. Bonne gestion budgétaire !
                </div>
              ) : (
                <KeywordsTable rows={filteredRows} mode={tab} multiClient={selectedClient === 'all'} />
              )}
            </div>

            {/* Closed-loop ROI per creative — Meta / LinkedIn Lead Ads */}
            <TopCreativesTable clientId={selectedClient} period="90d" />
          </>
        )}
      </div>
    </Layout>
  );
}

function KpiCard({ icon, label, value, hint, color = 'neutral', testId }) {
  const colors = {
    neutral: 'text-neutral-900',
    orange: 'text-orange-700',
    emerald: 'text-emerald-700',
    red: 'text-red-700',
  };
  const bgs = {
    neutral: 'bg-white',
    orange: 'bg-orange-50/50',
    emerald: 'bg-emerald-50/50',
    red: 'bg-red-50/50',
  };
  return (
    <div className={`${bgs[color]} border border-neutral-200 rounded-lg p-3`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-neutral-500 font-medium">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-xl font-bold mt-1.5 ${colors[color]}`}>{value}</div>
      {hint && <div className="text-[11px] text-neutral-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value, hint, color = 'neutral', bold = false }) {
  const colors = {
    emerald: 'text-emerald-700',
    red: 'text-red-700',
    neutral: 'text-neutral-900',
  };
  return (
    <div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">{label}</div>
      <div className={`${bold ? 'text-xl font-bold' : 'text-lg font-semibold'} mt-0.5 ${colors[color]}`}>{value}</div>
      {hint && <div className="text-[10px] text-neutral-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center ${
        active ? 'border-orange-600 text-orange-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function KeywordsTable({ rows, mode, multiClient }) {
  if (!rows.length) {
    return <div className="text-sm text-neutral-500 py-8 text-center bg-white border border-neutral-200 rounded">Aucun résultat.</div>;
  }
  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 border-b border-neutral-200">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Mot-clé</th>
            {multiClient && <th className="text-left px-3 py-2 font-medium text-neutral-600">Client</th>}
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Campagne</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Clics</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Coût</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Conv.</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Taux conv.</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">CPA</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">ROAS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.keyword}-${r.campaign_name}-${i}`} className="border-b border-neutral-50 hover:bg-neutral-50">
              <td className="px-3 py-2">
                <div className="font-medium">{r.keyword}</div>
                <div className="text-[10px] text-neutral-400 flex items-center gap-1">
                  <span>{r.match_type}</span>
                  {r.quality_score && (
                    <Badge className={`text-[9px] py-0 px-1 ${r.quality_score >= 7 ? 'bg-green-100 text-green-800' : r.quality_score >= 4 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                      QS {r.quality_score}/10
                    </Badge>
                  )}
                </div>
              </td>
              {multiClient && <td className="px-3 py-2 text-xs text-neutral-600">{r.client_name || '—'}</td>}
              <td className="px-3 py-2 text-xs text-neutral-600">
                <div>{r.campaign_name}</div>
                <div className="text-[10px] text-neutral-400">{r.ad_group_name}</div>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{formatNum(r.clicks)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEUR(r.cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{(r.conversions || 0).toFixed(1)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-neutral-500">
                {r.clicks ? formatPct(r.conversion_rate) : '—'}
              </td>
              <td className={`px-3 py-2 text-right tabular-nums ${mode === 'worst' ? 'text-red-600 font-medium' : ''}`}>
                {r.conversions ? formatEUR(r.cost_per_conversion) : (mode === 'worst' ? '∞' : '—')}
              </td>
              <td className={`px-3 py-2 text-right tabular-nums font-medium ${
                r.roas >= 2 ? 'text-emerald-700' : r.roas >= 1 ? 'text-amber-700' : r.roas > 0 ? 'text-red-600' : 'text-neutral-400'
              }`}>
                {r.roas ? formatRoas(r.roas) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
